import { useEffect, useState, useRef } from 'react';
import { SlideOver } from '../ui/SlideOver';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { useAIObservationHelper } from '../../hooks/useAIObservationHelper';
import { startObservationHelper, continueObservationHelper } from '../../api/eicrAI';
import { Sparkles, Loader2, AlertCircle, ChevronLeft } from 'lucide-react';

/**
 * AI Observation Helper slide-over for EICR observations.
 * Phases: prompt → questions (optional) → preview → Insert / Back / Cancel / Retry with GPT-5.2.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {Object} props.eicrContext - { premisesType, earthingArrangement, dbLocation, testResults, existingNotes }
 * @param {{ location: string|null, circuitRef: string|null }|null} props.observationSeed
 * @param {(draft: object) => void} props.onInsert - Called with draft when user clicks Insert
 * @param {string} [props.initialNotePrefill] - Pre-fill for initial prompt (e.g. obsDraft.text)
 */
export function AIObservationHelper({
  open,
  onOpenChange,
  eicrContext,
  observationSeed = null,
  onInsert,
  initialNotePrefill = '',
}) {
  const [promptInput, setPromptInput] = useState('');
  const hasOpenedRef = useRef(false);

  const {
    phase,
    initialNote,
    questions,
    answers,
    draft,
    error,
    modelUsed,
    escalationRequested,
    open: openFlow,
    reset,
    submitInitialNote,
    receiveStartResult,
    receiveStartError,
    setAnswer,
    submitAnswers,
    receiveContinueResult,
    receiveContinueError,
    insert,
    back,
    retry,
    isCollectingPrompt,
    isLoadingQuestions,
    isNeedsMoreInfo,
    isLoadingDraft,
    isReadyPreview,
    isError,
    isInserted,
    shouldEscalate,
  } = useAIObservationHelper();

  useEffect(() => {
    if (open) {
      hasOpenedRef.current = true;
      openFlow();
      setPromptInput(initialNotePrefill || '');
    } else {
      if (hasOpenedRef.current) reset();
      hasOpenedRef.current = false;
    }
  }, [open, openFlow, reset, initialNotePrefill]);

  const runStart = async (escalate = false) => {
    try {
      const res = await startObservationHelper(eicrContext, promptInput, observationSeed, { escalate });
      receiveStartResult({
        status: res.status,
        questions: res.questions,
        draft: res.draft,
        modelUsed: res.modelUsed,
      });
    } catch (err) {
      receiveStartError(err);
    }
  };

  const runContinue = async (escalate = false) => {
    try {
      const res = await continueObservationHelper(
        eicrContext,
        initialNote,
        answers,
        observationSeed,
        { escalate }
      );
      receiveContinueResult({
        status: res.status,
        questions: res.questions,
        draft: res.draft,
        modelUsed: res.modelUsed,
      });
    } catch (err) {
      receiveContinueError(err);
    }
  };

  const handleSubmitPrompt = (e) => {
    e.preventDefault();
    const note = promptInput?.trim();
    if (!note) return;
    submitInitialNote(note);
    runStart(shouldEscalate || escalationRequested);
  };

  const handleSubmitAnswers = (e) => {
    e.preventDefault();
    submitAnswers();
    runContinue(shouldEscalate || escalationRequested);
  };

  const handleInsert = () => {
    if (draft) onInsert?.(draft);
    insert();
    onOpenChange?.(false);
  };

  const handleBack = () => {
    back();
  };

  const handleCancel = () => {
    onOpenChange?.(false);
  };

  const handleRetry = () => {
    retry();
  };

  const handleRetryWithEscalate = () => {
    retry({ escalate: true });
  };

  const getTitle = () => {
    if (isLoadingQuestions || isLoadingDraft) return 'AI Observation Helper';
    if (isError) return 'Something went wrong';
    if (isReadyPreview) return 'Preview observation';
    if (isNeedsMoreInfo) return 'A few more details';
    return 'AI Observation Helper';
  };

  return (
    <SlideOver
      open={open}
      onOpenChange={onOpenChange}
      title={getTitle()}
      width="w-full max-w-2xl"
    >
      <div className="flex flex-col gap-4">
        {/* Phase A: Collect prompt */}
        {isCollectingPrompt && (
          <form onSubmit={handleSubmitPrompt} className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Describe what you found (e.g. &quot;No RCD on socket circuit in kitchen&quot;). The AI will suggest a code, observation text, remedial action, and regulation references.
            </p>
            <Textarea
              value={promptInput}
              onChange={(e) => setPromptInput(e.target.value)}
              placeholder="e.g. Missing earthing to metal back box, kitchen DB"
              rows={4}
              className="text-xs border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              required
            />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={!promptInput?.trim()}>
                <Sparkles className="w-4 h-4 mr-2" />
                Generate
              </Button>
            </div>
          </form>
        )}

        {/* Loading: questions */}
        {isLoadingQuestions && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Analysing your note…</p>
          </div>
        )}

        {/* Phase B: Questions */}
        {isNeedsMoreInfo && (
          <form onSubmit={handleSubmitAnswers} className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Please answer these to improve the suggestion:
            </p>
            <div className="space-y-4">
              {questions.map((q) => (
                <div key={q.key}>
                  <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {q.prompt}
                    {q.required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  {q.type === 'boolean' && (
                    <div className="flex gap-4">
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="radio"
                          name={q.key}
                          checked={answers[q.key] === true}
                          onChange={() => setAnswer(q.key, true)}
                          className="border-gray-300 dark:border-gray-600 text-blue-600"
                        />
                        Yes
                      </label>
                      <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="radio"
                          name={q.key}
                          checked={answers[q.key] === false}
                          onChange={() => setAnswer(q.key, false)}
                          className="border-gray-300 dark:border-gray-600 text-blue-600"
                        />
                        No
                      </label>
                    </div>
                  )}
                  {q.type === 'enum' && (
                    <Select
                      name={q.key}
                      value={answers[q.key] ?? ''}
                      onChange={(e) => setAnswer(q.key, e.target.value)}
                      className="h-8 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Select…</option>
                      {(q.options || []).map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </Select>
                  )}
                  {q.type === 'string' && (
                    <Input
                      type="text"
                      value={answers[q.key] ?? ''}
                      onChange={(e) => setAnswer(q.key, e.target.value)}
                      placeholder="Your answer"
                      className="h-8 border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  )}
                  {q.type === 'multiselect' && (
                    <div className="space-y-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 p-3">
                      {(q.options || []).map((opt) => (
                        <label
                          key={opt}
                          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                        >
                          <input
                            type="checkbox"
                            checked={(answers[q.key] ?? []).includes(opt)}
                            onChange={(e) => {
                              const prev = answers[q.key] ?? [];
                              const next = e.target.checked
                                ? [...prev, opt]
                                : prev.filter((x) => x !== opt);
                              setAnswer(q.key, next);
                            }}
                            className="border-gray-300 dark:border-gray-600 rounded text-blue-600"
                          />
                          {opt}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button type="submit" size="sm">
                Generate draft
              </Button>
            </div>
          </form>
        )}

        {/* Loading: draft */}
        {isLoadingDraft && (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <Loader2 className="w-10 h-10 animate-spin text-blue-500" />
            <p className="text-sm text-gray-600 dark:text-gray-400">Generating draft…</p>
          </div>
        )}

        {/* Phase C: Preview */}
        {isReadyPreview && draft && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Suggested code</h4>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge
                  variant={draft.suggested_code?.code === 'C1' || draft.suggested_code?.code === 'C2' ? 'danger' : 'warning'}
                  className="text-sm"
                >
                  {draft.suggested_code?.code ?? '—'}
                </Badge>
                {typeof draft.suggested_code?.confidence === 'number' && (
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {(draft.suggested_code.confidence * 100).toFixed(0)}% confidence
                  </span>
                )}
              </div>
              {draft.suggested_code?.rationale && (
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{draft.suggested_code.rationale}</p>
              )}
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Observation text</h4>
              <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                {draft.observation_text ?? '—'}
              </p>
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Recommended action</h4>
              <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md p-3">
                {draft.recommended_action ?? '—'}
              </p>
            </div>
            {Array.isArray(draft.reg_references) && draft.reg_references.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Regulation references</h4>
                <div className="flex flex-wrap gap-1.5">
                  {draft.reg_references.map((ref, i) => (
                    <Badge key={i} variant="neutral" className="text-xs">
                      {ref}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {modelUsed && (
              <p className="text-xs text-gray-500 dark:text-gray-400">Model: {modelUsed}</p>
            )}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button size="sm" onClick={handleInsert}>
                Insert
              </Button>
              <Button variant="outline" size="sm" onClick={handleBack}>
                <ChevronLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
              <Button variant="outline" size="sm" onClick={handleRetryWithEscalate}>
                Retry with GPT-5.2
              </Button>
            </div>
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 p-3 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={handleRetry}>
                Retry
              </Button>
              <Button variant="outline" size="sm" onClick={handleRetryWithEscalate}>
                Retry with GPT-5.2
              </Button>
              <Button variant="outline" size="sm" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {isInserted && null}
      </div>
    </SlideOver>
  );
}

export default AIObservationHelper;
